<template>
  <section
    class="message-templates-table flex h-full w-full overflow-auto bg-white dark:bg-gray-900"
  >
    <ve-table
      v-show="isTableVisible"
      ref="message-templates-table"
      class="message-templates-table-wrap"
      :class="isEmpty ? 'hidden' : 'h-full flex-1 overflow-x-hidden'"
      :fixed-header="true"
      scroll-width="80rem"
      :columns="columns"
      :table-data="processedTableData"
      :border-around="false"
      row-key-field-name="_id"
      :sort-option="sortOption"
      :cell-selection-option="cellSelectionOption"
      :cell-style-option="cellStyleOption"
      :event-custom-option="eventCustomOption"
    />

    <loading-state
      v-if="isLoading"
      class="w-full"
      :message="$t('CHATLYN_MESSAGE_TEMPLATE.LIST.LOADING_MESSAGE')"
    />

    <empty-state
      v-else-if="isEmpty"
      :title="$t('CHATLYN_MESSAGE_TEMPLATE.LIST.NO_MESSAGE_TEMPLATES')"
    />
  </section>
</template>

<script>
// Libs //
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import { format, parseISO } from 'date-fns';

// Mixins //
import templatesMixin from '../../../../mixins/templatesMixin';

// Components //
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';

// Constants //
import {
  MESSAGE_TEMPLATES_CHANNEL_TYPE,
  MESSAGE_TEMPLATES_STATUSES_OPTION,
} from '../constants';

const COLUMN_WIDTH_TITLE = 300;
const COLUMN_WIDTH_DEFAULT = 188;
const DATE_FORMAT = 'dd-MM-yyyy';
const TABLE_ROW_HIGHLIGHT_CLASS = 've-table-tr-highlight';

export default {
  name: 'MessageTemplatesTable',

  components: {
    LoadingState,
    VeTable,
    EmptyState,
  },

  mixins: [templatesMixin],

  props: {
    sortConfig: {
      type: Object,
      default: () => ({}),
    },

    messageTemplates: {
      type: Array,
      default: () => [],
    },

    messageTemplateType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      sortOption: {
        sortAlways: true,
        sortChange: (params) => this.$emit('on-sort-change', params),
      },
      cellSelectionOption: {
        // disble cell selection
        enable: false,
      },
      cellStyleOption: {
        bodyCellClass: ({ row }) =>
          this.isRowDisabled(row) ? 'disabled-cell' : '',
      },
      eventCustomOption: {
        // eslint-disable-next-line no-unused-vars
        bodyRowEvents: ({ row }) => {
          return {
            click: () => {
              if (row.isDisabled) {
                this.onDisabledRowClick(row);
                return;
              }

              // eslint-disable-next-line no-underscore-dangle
              const currentRowKey = row._id;
              this.onRowClick(currentRowKey);
            },
          };
        },
      },
    };
  },

  computed: {
    ...mapGetters({
      whatsAppInboxes: 'inboxes/getWhatsAppInboxes',
      uiFlags: 'messageTemplates/getUIFlags',
      isPhoenix: 'accounts/isPhoenix',
    }),

    processedTableData() {
      return this.messageTemplates.map((row) => ({
        ...row,
        isDisabled: this.isRowDisabled(row), // Add disabled flag
      }));
    },

    columns() {
      return [
        {
          field: 'title',
          key: 'title',
          rowKey: 'title',
          title: this.$t('CHATLYN_MESSAGE_TEMPLATE.LIST.TABLE_HEADER.TITLE'),
          fixed: 'left',
          align: 'left',
          sortBy: this.sortConfig.title || '',
          width: COLUMN_WIDTH_TITLE,
          renderBodyCell: ({ row }) => (
            <a
              class="stretched-link flex items-center gap-2"
              // eslint-disable-next-line no-underscore-dangle
            >
              <div class="overflow-hidden text-ellipsis whitespace-nowrap text-left text-base">
                {row.title}
              </div>
            </a>
          ),
        },
        {
          field: 'translations',
          rowKey: 'translations',
          key: 'translations',
          title: this.$t(
            'CHATLYN_MESSAGE_TEMPLATE.LIST.TABLE_HEADER.LANGUAGES'
          ),
          align: 'left',
          sortBy: this.sortConfig.translations || '',
          width: COLUMN_WIDTH_DEFAULT,
          renderBodyCell: ({ row }) => {
            return (
              <div class="message-cell-languages">
                {row.isDisabled ? (
                  <span class="message-template-lang-badge">
                    <span class="lang-name">
                      {this.$t('CHATLYN_GENERAL.MESSAGES.DELETING')}
                    </span>
                  </span>
                ) : (
                  row.translations.map((translation) => {
                    const status = (
                      translation?.status ||
                      row?.status ||
                      ''
                    ).toLowerCase();

                    const statusOption =
                      MESSAGE_TEMPLATES_STATUSES_OPTION[status];

                    const handleOnClick = () => {
                      if (row.isDisabled) {
                        this.onDisabledRowClick(row);
                        return;
                      }

                      // eslint-disable-next-line no-underscore-dangle
                      const currentRowKey = row._id;
                      this.onRowClick(currentRowKey, translation.language);
                    };

                    return (
                      <v-popover
                        trigger="hover"
                        placement="right"
                        offset="10"
                        disabled={!statusOption?.title}
                      >
                        <span
                          class="message-template-lang-badge"
                          onClick={handleOnClick}
                        >
                          {statusOption?.color ? (
                            <span
                              class="status-indicator "
                              style={`background-color: ${statusOption?.color}`}
                            />
                          ) : (
                            ''
                          )}

                          <span class="lang-name">
                            {translation.language.toUpperCase()}
                          </span>
                        </span>

                        <template slot="popover">
                          <div class="tooltip-content">
                            <span>{this.$t(statusOption?.title)}</span>
                          </div>
                        </template>
                      </v-popover>
                    );
                  })
                )}
              </div>
            );
          },
        },
        {
          field: 'inboxType',
          rowKey: 'inboxType',
          key: 'inboxType',
          title: this.$t(
            'CHATLYN_MESSAGE_TEMPLATE.LIST.TABLE_HEADER.INBOX_TYPE'
          ),
          align: 'left',
          sortBy: this.sortConfig.inboxType || '',
          width: COLUMN_WIDTH_DEFAULT,
          renderBodyCell: ({ row }) => {
            let inboxType = '';
            let text = '';

            switch (this.messageTemplateType) {
              case MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP: {
                inboxType = this.$t(
                  'CHATLYN_MESSAGE_TEMPLATE.LIST.INBOX_TYPE.WHATSAPP'
                );

                if (this.whatsAppInboxes.length > 1) {
                  const whatsAppInbox = this.getWhatsAppInboxByNumber(
                    `+${row.phoneNumber}`
                  );

                  const whatsAppInboxPhoneNumber =
                    whatsAppInbox?.phone_number ||
                    `+${whatsAppInbox?.additional_attributes?.number}`;

                  text = whatsAppInbox
                    ? `${whatsAppInbox.name} ${whatsAppInboxPhoneNumber}`
                    : this.$t(
                        'CHATLYN_MESSAGE_TEMPLATE.LIST.NO_WHATS_APP_INBOX'
                      );
                }

                break;
              }

              case MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS: {
                inboxType = this.$t(
                  'CHATLYN_MESSAGE_TEMPLATE.LIST.INBOX_TYPE.SMS'
                );
                // For Phoenix accounts, show senderName below SMS in grey
                if (this.isPhoenix && row.senderName) {
                  text = row.senderName;
                }
                break;
              }

              default:
                break;
            }

            return (
              <span class="message-cell-inbox w-full">
                <span>{inboxType}</span>
                {text ? <span class="text-gray-500">({text})</span> : ''}
              </span>
            );
          },
        },
        {
          field: 'createdAt',
          rowKey: 'createdAt',
          key: 'createdAt',
          title: this.$t(
            'CHATLYN_MESSAGE_TEMPLATE.LIST.TABLE_HEADER.CREATED_AT'
          ),
          align: 'left',
          sortBy: this.sortConfig.createdAt || '',
          width: COLUMN_WIDTH_DEFAULT,
          renderBodyCell: ({ row }) => {
            return format(parseISO(row.createdAt), DATE_FORMAT);
          },
        },
      ];
    },

    isEmpty() {
      return Boolean(
        !this.uiFlags.isFetching && !this.messageTemplates?.length
      );
    },

    isLoading() {
      return this.uiFlags.isFetching;
    },

    isTableVisible() {
      return !this.isLoading && !this.isEmpty;
    },
  },

  methods: {
    isRowDisabled(row) {
      // eslint-disable-next-line no-underscore-dangle
      return this.isTemplateDeleting(row._id);
    },

    getWhatsAppInboxByNumber(number) {
      if (!this.whatsAppInboxes.length) {
        return null;
      }

      return this.whatsAppInboxes.find(
        (whatsAppInbox) =>
          whatsAppInbox.phone_number === number ||
          `+${whatsAppInbox?.additional_attributes?.number}` === number
      );
    },

    onDisabledRowClick(row) {
      this.removeHighlight(row);
      this.$emit('hide-info-panel');
    },

    onRowClick(messageTemplateId, language) {
      if (language) {
        this.$emit('change-selected-translation-language', language);
      }

      this.$emit('select-message-template', messageTemplateId);
    },

    removeHighlight(row) {
      setTimeout(() => {
        const table = this.$refs['message-templates-table'];

        const element = table?.$el?.querySelector(
          // eslint-disable-next-line no-underscore-dangle
          `[row-key="${row._id}"]`
        );

        if (!element) {
          return;
        }

        element.classList.remove(TABLE_ROW_HIGHLIGHT_CLASS);
      }, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
.message-templates-table-wrap {
  ::v-deep {
    .message-template-lang-badge {
      @apply py-0.5 ps-1;
    }
    .message-cell-inbox {
      @apply flex flex-col flex-nowrap;
    }
    .message-cell-languages {
      @apply flex flex-row gap-2 overflow-auto;
    }

    .disabled-cell,
    .disabled-cell *,
    .disabled-cell *::after {
      pointer-events: none !important;
      --tw-text-opacity: 0.5 !important;
    }
  }
}
</style>
